'use client';

import { type CSSProperties, type MouseEvent } from 'react';

import { ContactCardView } from '@ps/features/contact-card/components/ContactCardView';
import type { ContactCardViewModel } from '@ps/features/contact-card/functions/contactCardViewModel';
import type { BioContext } from '@ps/functions/bio/bioContext';
import { ArrowLeft } from 'lucide-react';

interface Props {
  vm: ContactCardViewModel;
  /** The page's bio context — makes the card's links custom-domain aware. */
  bioContext: BioContext;
  /** Creator bio home — where Back lands. */
  bioHref: string;
  creatorName: string;
  /** Creator backdrop (image / gradient / color) painted behind the centered card. */
  pageBackground: CSSProperties;
}

/**
 * Standalone host for `/<url_code>/contact` — the cold-load / direct-link focus
 * view. NOT a dialog: it renders {@link ContactCardView} in a centered dark card
 * on the creator's bio backdrop (like the tipping / post standalone pages). Back
 * leaves via a hard `location.replace` so a subsequent browser Back skips this
 * route.
 */
export function ContactStandalone({
  vm,
  bioContext,
  bioHref,
  creatorName,
  pageBackground,
}: Props) {
  const leaveToBio = (event: MouseEvent) => {
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }
    event.preventDefault();
    window.location.replace(bioHref);
  };

  return (
    <div className="relative flex min-h-dvh w-full items-center justify-center p-4">
      <div className="fixed inset-0 -z-10" style={pageBackground} aria-hidden />

      <a
        href={bioHref}
        onClick={leaveToBio}
        aria-label={`Back to ${creatorName}`}
        className="fixed left-3 top-3 z-20 flex h-9 items-center gap-2 rounded-full bg-black/40 px-3 text-sm font-medium text-white transition hover:bg-black/60"
      >
        <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
        <span className="max-w-[12rem] truncate">{creatorName}</span>
      </a>

      <div className="flex max-h-[90vh] w-full max-w-[440px] flex-col rounded-xl bg-black text-white shadow-2xl">
        <ContactCardView
          vm={vm}
          bioContext={bioContext}
          className="flex-1 p-5"
        />
      </div>
    </div>
  );
}
